import { getCustomerClient } from "@fermata/grpc-zustand/customer"
import { CustomerGroup } from "@proto/gen/users/v1/service"
import { PlatformRole, type UserTenant } from "@proto/gen/users/v1/service"
import type React from "react"
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react"
import { PageLoader } from "../components/ui/page-loader"
import { useUiStore } from "../stores/useUiStore"
import type { AccessibleCustomer, AuthContextType, User } from "../types/auth"
import type { UserPreferences } from "../types/user-preferences"

/** Returns the token's expiry timestamp in ms, or null if unparseable. */
function getJwtExpiryMs(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")))
    return typeof payload.exp === "number" ? payload.exp * 1000 : null
  } catch {
    return null
  }
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// API base URL from environment or default to local
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000"

/**
 * Backoff for a silent refresh that failed for a transient reason. The refresh
 * cookie outlives the access token by days, so retrying is what keeps a
 * backend blip from quietly ending the session: the schedule only re-arms
 * itself on success, so without this one failed call stops all further refresh.
 */
const REFRESH_RETRY_DELAYS_MS = [5_000, 15_000, 45_000, 120_000, 300_000]

/**
 * Delay before refresh retry `attempt`, holding at the final value instead of
 * running off the end of the list. An outage outlasting the schedule would
 * otherwise leave no timer armed and strand a session whose refresh cookie is
 * still good for days — the exact failure the backoff exists to prevent.
 */
function refreshRetryDelayMs(attempt: number): number {
  return REFRESH_RETRY_DELAYS_MS[Math.min(attempt, REFRESH_RETRY_DELAYS_MS.length - 1)]
}

/** Backoff for a `/me` that failed transiently (network error or 5xx). */
const ME_RETRY_DELAYS_MS = [1_000, 3_000, 8_000, 20_000]

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Fetches `/v1/auth/me`, retrying transient failures instead of reporting them
 * to the caller. A rolling backend deploy briefly answers 502/503, and dropping
 * the user on the login screen for that reads as a random logout even though
 * their session is perfectly valid.
 */
async function fetchCurrentUser(token: string | null): Promise<Response> {
  const headers = token ? { Authorization: `Bearer ${token}` } : undefined
  for (let attempt = 0; ; attempt++) {
    const lastAttempt = attempt >= ME_RETRY_DELAYS_MS.length
    try {
      const response = await fetch(`${API_BASE_URL}/v1/auth/me`, { headers })
      if (response.status < 500 || lastAttempt) return response
    } catch (err) {
      if (lastAttempt) throw err
    }
    await sleep(ME_RETRY_DELAYS_MS[attempt])
  }
}

// Bypass auth for development - set via environment variable
const BYPASS_AUTH = import.meta.env.VITE_BYPASS_AUTH === "true"

interface AuthProviderProps {
  children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [userTenants, setUserTenants] = useState<UserTenant[]>([])
  const [accessibleCustomers, setAccessibleCustomers] = useState<AccessibleCustomer[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const preferencesTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Bumped by logout so a token refresh already in flight when the user logs out
  // won't resurrect a ghost session by persisting a freshly minted token after.
  const sessionEpochRef = useRef(0)

  const applyServerPreferences = useCallback((preferences?: UserPreferences) => {
    if (preferences) {
      useUiStore.getState().applyServerPreferences(preferences)
    }
  }, [])

  /** Clears all client-side session state and stored tokens. */
  const clearSession = useCallback(() => {
    if (typeof window !== "undefined") {
      localStorage.removeItem("auth_token")
      localStorage.removeItem("auth_access_token")
    }
    setUser(null)
    setUserTenants([])
  }, [])

  /** Schedules a silent token refresh 2 minutes before the JWT expires. */
  const scheduleTokenRefresh = useCallback(
    (token: string) => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
      const expiryMs = getJwtExpiryMs(token)
      if (!expiryMs) return
      const msUntilRefresh = expiryMs - Date.now() - 2 * 60 * 1000
      if (msUntilRefresh <= 0) return

      // Re-arm on a transient failure. Retrying past the access token's own
      // expiry is still worth it: the refresh cookie is valid for days, so a
      // later attempt restores the session rather than stranding the user.
      // Logging out cannot cancel a refresh that is already in flight, and this
      // loop no longer gives up on its own, so pin the session it belongs to.
      const epoch = sessionEpochRef.current

      function retryLater(attempt: number) {
        if (sessionEpochRef.current !== epoch) return
        refreshTimerRef.current = setTimeout(() => run(attempt + 1), refreshRetryDelayMs(attempt))
      }

      function run(attempt: number) {
        fetch(`${API_BASE_URL}/v1/auth/refresh`, { method: "POST", credentials: "include" })
          .then(async (r) => {
            // A logout, and possibly a fresh login, can land while this is in
            // flight. This answer is about the session that asked for it, so
            // acting on a 401 here would sign out whoever is logged in now.
            if (sessionEpochRef.current !== epoch) return
            if (r.status === 401 || r.status === 403) {
              // Refresh token is expired or revoked — clear the stale access token so
              // the next gRPC/REST call gets "Missing authorization" instead of
              // silently sending a dead Bearer token.
              clearSession()
              return
            }
            // Transient server error — leave the token/session intact and retry.
            if (!r.ok) {
              retryLater(attempt)
              return
            }
            const data = await r.json()
            // Reading the body is its own await, so the session can turn over
            // here too. A stored token no longer proves the session is ours —
            // after a logout and a fresh login it belongs to whoever is on now,
            // and writing over it would hand them the old account's token.
            if (sessionEpochRef.current !== epoch) return
            const newToken = data?.idToken || data?.accessToken
            // The stored token still matters: checkSession can end a session
            // without a logout, and writing here would revive it.
            if (newToken && typeof window !== "undefined" && localStorage.getItem("auth_token")) {
              localStorage.setItem("auth_token", newToken)
              if (data?.accessToken) localStorage.setItem("auth_access_token", data.accessToken)
              scheduleTokenRefresh(newToken)
            }
          })
          .catch(() => {
            // Network-level failure (no response) — session stays intact, retry.
            retryLater(attempt)
          })
      }

      refreshTimerRef.current = setTimeout(() => run(0), msUntilRefresh)
    },
    [clearSession]
  )

  /**
   * Mints a fresh access/id token from the httpOnly refresh cookie.
   * Returns the new token on success, `null` when the refresh token itself is
   * expired/revoked (the caller should log the user out), or `undefined` on a
   * transient/network failure (the caller should keep the current session).
   */
  const attemptTokenRefresh = useCallback(async (): Promise<string | null | undefined> => {
    const epoch = sessionEpochRef.current
    try {
      const response = await fetch(`${API_BASE_URL}/v1/auth/refresh`, {
        method: "POST",
        credentials: "include",
      })
      if (response.status === 401 || response.status === 403) return null
      if (!response.ok) return undefined
      const data = await response.json().catch(() => null)
      const newToken = data?.idToken || data?.accessToken
      // Persist unless a logout happened while this request was in flight (the
      // epoch bump). Keying on the epoch rather than on a pre-existing stored
      // token keeps recovery sticky when the user arrives with only a valid
      // refresh cookie and no access token, while still avoiding a ghost session
      // if logout() cleared state mid-refresh.
      if (newToken && typeof window !== "undefined" && sessionEpochRef.current === epoch) {
        localStorage.setItem("auth_token", newToken)
        if (data?.accessToken) localStorage.setItem("auth_access_token", data.accessToken)
        scheduleTokenRefresh(newToken)
      }
      return newToken ?? undefined
    } catch {
      return undefined
    }
  }, [scheduleTokenRefresh])

  const checkSession = useCallback(async () => {
    try {
      setIsLoading(true)

      let token = typeof window !== "undefined" ? localStorage.getItem("auth_token") : null

      // If the stored token has already expired (e.g. the tab was backgrounded or
      // the machine slept, so the scheduled silent refresh never fired), renew it
      // via the refresh cookie *before* calling /me. Otherwise a still-valid
      // long-lived session would be discarded on the next page load.
      if (token) {
        const expiryMs = getJwtExpiryMs(token)
        if (expiryMs && expiryMs <= Date.now()) {
          const refreshed = await attemptTokenRefresh()
          if (refreshed === null) {
            clearSession()
            return
          }
          if (refreshed) token = refreshed
        } else {
          scheduleTokenRefresh(token)
        }
      }

      let response = await fetchCurrentUser(token)

      // The access token was rejected. Attempt a single refresh via the refresh
      // cookie and retry /me once before giving up on the session.
      if (response.status === 401 || response.status === 403) {
        const refreshed = await attemptTokenRefresh()
        if (refreshed === null) {
          // Refresh token is expired/revoked — genuinely logged out.
          clearSession()
          return
        }
        if (refreshed === undefined) {
          // Transient/network refresh failure: don't discard a session whose
          // refresh token may still be valid. Leave the tokens intact and let
          // the next interaction or visibility refresh retry.
          setError(new Error("Could not reach the authentication service"))
          return
        }
        token = refreshed
        response = await fetchCurrentUser(token)
      }

      if (response.ok) {
        const data = await response.json()
        if (data.success && data.user) {
          setUser({
            id: data.user.id,
            email: data.user.email,
            name:
              `${data.user.givenName || ""} ${data.user.familyName || ""}`.trim() ||
              data.user.email,
            givenName: data.user.givenName,
            familyName: data.user.familyName,
            avatar: data.user.pictureUrl,
            isGlobalAdmin: data.user.isGlobalAdmin,
            platformRole: data.user.platformRole,
          })
          setUserTenants(data.userTenants || [])
          applyServerPreferences(data.preferences)
        } else {
          clearSession()
        }
      } else if (response.status === 401 || response.status === 403) {
        clearSession()
      } else {
        // The backend is still failing after the retries above. With no user we
        // cannot render the app, so AuthGuard will send them to the login
        // screen — but the stored tokens are almost certainly fine, so keep them
        // and a reload restores the session once the backend is healthy.
        setError(new Error(`Could not reach the authentication service (${response.status})`))
        setUser(null)
        setUserTenants([])
      }
    } catch (err) {
      console.error("Session check failed:", err)
      // Same reasoning as above: a network failure is not a signed-out session,
      // so the tokens stay put.
      setError(err as Error)
      setUser(null)
      setUserTenants([])
    } finally {
      setIsLoading(false)
    }
  }, [attemptTokenRefresh, scheduleTokenRefresh, clearSession, applyServerPreferences])

  useEffect(() => {
    if (BYPASS_AUTH) {
      setUser({
        id: "dev-user-001",
        email: "dev@fermata.com",
        name: "John Smith",
        avatar: undefined,
        isGlobalAdmin: true,
        // Dev bypass acts as a full global admin. To exercise the restricted
        // global_ops admin gating locally, set this to PlatformRole.GLOBAL_OPS.
        platformRole: PlatformRole.GLOBAL_ADMIN,
      })
      setIsLoading(false)
    } else {
      checkSession()
    }
  }, [checkSession])

  // A setTimeout-based silent refresh does not fire reliably while the tab is
  // backgrounded or the machine is asleep. When the tab becomes visible again,
  // proactively refresh if the token is expired or within the pre-expiry window
  // so the user isn't logged out on their next interaction.
  useEffect(() => {
    if (BYPASS_AUTH || typeof document === "undefined") return
    const handleVisibility = () => {
      if (document.visibilityState !== "visible") return
      const token = typeof window !== "undefined" ? localStorage.getItem("auth_token") : null
      if (!token) return
      const expiryMs = getJwtExpiryMs(token)
      if (!expiryMs || expiryMs - Date.now() <= 2 * 60 * 1000) {
        void attemptTokenRefresh().then((refreshed) => {
          if (refreshed === null) clearSession()
        })
      }
    }
    document.addEventListener("visibilitychange", handleVisibility)
    return () => document.removeEventListener("visibilitychange", handleVisibility)
  }, [attemptTokenRefresh, clearSession])

  // The refresh retry re-arms itself indefinitely, so it has to be stopped when
  // the provider goes away or it outlives the session it was refreshing.
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
    }
  }, [])

  const fetchAccessibleCustomers = async (tenantId: string) => {
    /**
     * Under dev bypass there is no token, so the REST endpoint 401s and the
     * customer app sat on "No Customers Available" while /admin worked. The
     * bypass identity is a global admin, whose access is not scoped by
     * user_customer links, so listing customers directly is the correct set.
     */
    if (BYPASS_AUTH) {
      try {
        const list = await getCustomerClient().listCustomers({
          tenantId,
          externalRef: "",
          parentCustomerId: "",
          includeDeleted: false,
          pagination: { offset: 0, limit: 200 },
        } as never)
        setAccessibleCustomers(
          (list.response.customers ?? []).map((customer) => ({
            customer,
            group: CustomerGroup.UNSPECIFIED,
          })) as AccessibleCustomer[]
        )
      } catch (err) {
        console.error("Failed to list customers for dev bypass:", err)
        setAccessibleCustomers([])
      }
      return
    }

    try {
      setError(null)

      const token =
        !BYPASS_AUTH && typeof window !== "undefined" ? localStorage.getItem("auth_token") : null

      const response = await fetch(
        `${API_BASE_URL}/v1/auth/accessible-customers?tenant_id=${tenantId}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        }
      )

      if (!response.ok) {
        throw new Error("Failed to fetch accessible customers")
      }

      const data = await response.json()
      if (data.success && Array.isArray(data.accessibleCustomers)) {
        setAccessibleCustomers(data.accessibleCustomers)
      } else {
        setAccessibleCustomers([])
      }
    } catch (err) {
      // This list only powers the customer switcher. Promoting its failure to
      // the global auth error replaced the whole app with "Something went
      // wrong" whenever the backend hiccuped, even though the user is
      // authenticated and every other view still works.
      console.error("Failed to fetch accessible customers:", err)
      setAccessibleCustomers([])
    }
  }

  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true)
      setError(null)

      if (BYPASS_AUTH) {
        setUser({
          id: "dev-user-001",
          email,
          name: email.split("@")[0],
          avatar: undefined,
          isGlobalAdmin: true,
          platformRole: PlatformRole.GLOBAL_ADMIN,
        })
        return {}
      }

      console.log("Checking session via API:", `${API_BASE_URL}/v1/auth/me`)
      const response = await fetch(`${API_BASE_URL}/v1/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
        credentials: "include",
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || "Login failed")
      }

      const data = await response.json().catch(() => ({}))
      // First login with a temporary password: the server returns a challenge
      // (no tokens yet). The caller completes it via completeNewPassword.
      if (data.success && data.challenge) {
        return { challenge: data.challenge, session: data.session }
      }
      if (data.success && data.user) {
        const token = data.idToken || data.accessToken
        if (token) {
          localStorage.setItem("auth_token", token)
          scheduleTokenRefresh(token)
        }
        // Persist the access token separately. Scope-validated API Gateway
        // routes (e.g. device registration) require the access token, which
        // carries the OAuth `scope` claim that ID tokens lack.
        if (data.accessToken) {
          localStorage.setItem("auth_access_token", data.accessToken)
        }

        setUser({
          id: data.user.id,
          email: data.user.email,
          name:
            `${data.user.givenName || ""} ${data.user.familyName || ""}`.trim() || data.user.email,
          givenName: data.user.givenName,
          familyName: data.user.familyName,
          avatar: data.user.pictureUrl,
          isGlobalAdmin: data.user.isGlobalAdmin,
          platformRole: data.user.platformRole,
        })
        setUserTenants(data.userTenants || [])
        applyServerPreferences(data.preferences)
        // Clear any stale customers from a prior session — a login that
        // follows another login (without an intervening logout) would
        // otherwise inherit the previous user's accessible customers.
        setAccessibleCustomers([])
        return {}
      }
      throw new Error("Login failed: Invalid response")
    } catch (err) {
      console.error("Login failed:", err)
      setError(err as Error)
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const logout = async () => {
    try {
      setIsLoading(true)
      setError(null)
      // Invalidate any refresh already in flight so it can't re-persist tokens.
      sessionEpochRef.current += 1

      if (BYPASS_AUTH) {
        setUser(null)
        return
      }

      const response = await fetch(`${API_BASE_URL}/v1/auth/logout`, {
        method: "POST",
        credentials: "include",
      }).catch(() => null)

      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
      if (typeof window !== "undefined") {
        localStorage.removeItem("auth_token")
        localStorage.removeItem("auth_access_token")
      }
      setUser(null)
      setUserTenants([])
      setAccessibleCustomers([])

      if (response && !response.ok) {
        console.error("Logout API failed, but cleared local session")
      }
    } catch (err) {
      console.error("Logout failed:", err)
      setError(err as Error)
      setUser(null)
      setUserTenants([])
      setAccessibleCustomers([])
    } finally {
      setIsLoading(false)
    }
  }

  const refreshSession = useCallback(async () => {
    if (BYPASS_AUTH) return

    // Only treat a confirmed-dead refresh token (null) as a logout. A transient
    // failure (undefined) falls through to checkSession, which re-validates and
    // retries rather than forcing the user out on a network hiccup.
    const refreshed = await attemptTokenRefresh()
    if (refreshed === null) {
      clearSession()
      const err = new Error("Session expired. Please log in again.")
      setError(err)
      throw err
    }

    await checkSession()
  }, [attemptTokenRefresh, clearSession, checkSession])

  /** Update the current user's own profile (name/picture) via PATCH /v1/auth/me. */
  const updateProfile = async (data: {
    givenName?: string
    familyName?: string
    pictureUrl?: string
  }) => {
    // Use the access token for consistency with the other authenticated calls
    // (e.g. changePassword); authMiddleware accepts the access token.
    const token = typeof window !== "undefined" ? localStorage.getItem("auth_access_token") : null
    const response = await fetch(`${API_BASE_URL}/v1/auth/me`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: "include",
      body: JSON.stringify(data),
    })
    const body = await response.json().catch(() => ({}))
    if (!response.ok || !body.success) {
      throw new Error(body.error || "Failed to update profile")
    }
    // Reflect the saved values locally (name shown in the top bar, etc.).
    const u = body.user
    if (u) {
      setUser((prev) =>
        prev
          ? {
              ...prev,
              name: `${u.givenName || ""} ${u.familyName || ""}`.trim() || prev.email,
              givenName: u.givenName,
              familyName: u.familyName,
              avatar: u.pictureUrl,
            }
          : prev
      )
    }
  }

  /** Change the current user's password via POST /v1/auth/change-password. */
  const changePassword = async (currentPassword: string, newPassword: string) => {
    // Cognito ChangePassword requires the ACCESS token (not the id token).
    const accessToken =
      typeof window !== "undefined" ? localStorage.getItem("auth_access_token") : null
    const response = await fetch(`${API_BASE_URL}/v1/auth/change-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      credentials: "include",
      body: JSON.stringify({ currentPassword, newPassword }),
    })
    const body = await response.json().catch(() => ({}))
    if (!response.ok || !body.success) {
      throw new Error(body.error || "Failed to change password")
    }
  }

  /** Complete a first-login NEW_PASSWORD_REQUIRED challenge → signs the user in. */
  const completeNewPassword = async (email: string, session: string, newPassword: string) => {
    const response = await fetch(`${API_BASE_URL}/v1/auth/complete-new-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ email, session, newPassword }),
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || !data.success || !data.user) {
      throw new Error(data.error || "Failed to set password")
    }
    const token = data.idToken || data.accessToken
    if (token && typeof window !== "undefined") {
      localStorage.setItem("auth_token", token)
      scheduleTokenRefresh(token)
    }
    if (data.accessToken && typeof window !== "undefined") {
      localStorage.setItem("auth_access_token", data.accessToken)
    }
    setUser({
      id: data.user.id,
      email: data.user.email,
      name: `${data.user.givenName || ""} ${data.user.familyName || ""}`.trim() || data.user.email,
      givenName: data.user.givenName,
      familyName: data.user.familyName,
      avatar: data.user.pictureUrl,
      isGlobalAdmin: data.user.isGlobalAdmin,
      platformRole: data.user.platformRole,
    })
    setUserTenants(data.userTenants || [])
    applyServerPreferences(data.preferences)
    setAccessibleCustomers([])
  }

  /** Persist partial user preferences to the server (debounced). */
  const updatePreferences = useCallback((patch: Partial<UserPreferences>) => {
    if (BYPASS_AUTH) return
    if (preferencesTimerRef.current) clearTimeout(preferencesTimerRef.current)
    preferencesTimerRef.current = setTimeout(async () => {
      const token =
        typeof window !== "undefined"
          ? localStorage.getItem("auth_access_token") || localStorage.getItem("auth_token")
          : null
      if (!token) return
      try {
        const response = await fetch(`${API_BASE_URL}/v1/auth/me/preferences`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          credentials: "include",
          body: JSON.stringify(patch),
        })
        const body = await response.json().catch(() => ({}))
        if (!response.ok || !body.success) {
          console.error("Failed to persist preferences:", body.error || response.statusText)
        }
      } catch (err) {
        console.error("Failed to persist preferences:", err)
      }
    }, 500)
  }, [])

  /** Begin a forgot-password reset (Cognito emails a code). */
  const requestPasswordReset = async (email: string) => {
    const response = await fetch(`${API_BASE_URL}/v1/auth/forgot-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    })
    const body = await response.json().catch(() => ({}))
    if (!response.ok || !body.success) {
      throw new Error(body.error || "Failed to start password reset")
    }
  }

  /** Complete a forgot-password reset with the emailed code + new password. */
  const confirmPasswordReset = async (email: string, code: string, newPassword: string) => {
    const response = await fetch(`${API_BASE_URL}/v1/auth/confirm-forgot-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, code, newPassword }),
    })
    const body = await response.json().catch(() => ({}))
    if (!response.ok || !body.success) {
      throw new Error(body.error || "Failed to reset password")
    }
  }

  const value: AuthContextType = {
    user,
    userTenants,
    accessibleCustomers,
    isAuthenticated: !!user,
    isLoading,
    error,
    login,
    logout,
    refreshSession,
    fetchAccessibleCustomers,
    updateProfile,
    changePassword,
    completeNewPassword,
    updatePreferences,
    requestPasswordReset,
    confirmPasswordReset,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}

// HOC for protected routes
export function withAuth<P extends object>(Component: React.ComponentType<P>) {
  return function AuthenticatedComponent(props: P) {
    const { isAuthenticated, isLoading } = useAuth()

    if (isLoading) {
      return <PageLoader className="min-h-screen" />
    }

    if (!isAuthenticated) {
      return (
        <div className="flex h-screen items-center justify-center">
          <div className="text-content-2">Please log in to continue</div>
        </div>
      )
    }

    return <Component {...props} />
  }
}
